import React from 'react';
import {
    ListItem,
    ListItemSecondaryAction,
    IconButton,
    Box,
    Typography,
    Chip,
    Tooltip
} from '@mui/material';
import { Visibility, Edit, Translate, MoreVert, CheckCircle, Warning, Article } from '@mui/icons-material';
import PropTypes from 'prop-types';

const AlgorithmicProblemListItem = ({ 
    problem, 
    orderNumber, 
    onView, 
    onEdit, 
    onTranslate, 
    onEditContent,
    onMenuOpen 
}) => {
    if (!problem) {
        return null;
    }

    const getDifficultyColor = (difficulty) => {
        switch (difficulty) {
            case 'easy':
                return 'success';
            case 'medium':
                return 'warning';
            case 'hard':
                return 'error';
            default:
                return 'default';
        }
    };

    return (
        <ListItem sx={{ border: 1, borderColor: 'divider', mb: 1, borderRadius: 1 }}>
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                minWidth: 40,
                mr: 2,
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                borderRadius: '50%',
                width: 32,
                height: 32,
                fontSize: '0.875rem',
                fontWeight: 'bold'
            }}>
                {orderNumber}
            </Box>
            <Box sx={{ flexGrow: 1, ml: 2 }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h6" component="div">
                        {problem.title}
                    </Typography>
                    <Tooltip title={problem.isReadyToServe ? "Ready to serve to users" : "Not ready to serve"}>
                        {problem.isReadyToServe ? (
                            <CheckCircle color="success" fontSize="small" />
                        ) : (
                            <Warning color="warning" fontSize="small" />
                        )}
                    </Tooltip>
                </Box>
                <Typography variant="body2" color="text.secondary">
                    {problem.language?.name}
                    {problem.level?.name && ` • ${problem.level.name}`}
                    {problem.difficulty && (
                        <Chip 
                            label={problem.difficulty.toUpperCase()} 
                            size="small" 
                            color={getDifficultyColor(problem.difficulty)}
                            variant="outlined"
                            sx={{ ml: 1, fontSize: '0.7rem', height: '20px' }}
                        />
                    )}
                    {problem.isReadyToServe ? (
                        <Chip 
                            label="Ready" 
                            size="small" 
                            color="success" 
                            variant="outlined"
                            sx={{ ml: 1, fontSize: '0.7rem', height: '20px' }}
                        />
                    ) : (
                        <Chip 
                            label="Draft" 
                            size="small" 
                            color="warning" 
                            variant="outlined"
                            sx={{ ml: 1, fontSize: '0.7rem', height: '20px' }}
                        />
                    )}
                </Typography>
                {problem.problemUrl && (
                    <Typography variant="body2" color="text.secondary">
                        <a href={problem.problemUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                            {problem.problemUrl}
                        </a>
                    </Typography>
                )}
                
                <Box sx={{ mt: 1 }}>
                    {problem.tags && problem.tags.map((tag, index) => (
                        <Chip
                            key={index}
                            label={tag}
                            size="small"
                            variant="outlined"
                            sx={{ mr: 0.5, mb: 0.5 }}
                        />
                    ))}
                </Box>
            </Box>
            <ListItemSecondaryAction>
                <IconButton
                    edge="end"
                    aria-label="view"
                    onClick={() => onView(problem)}
                    sx={{ mr: 1 }}
                >
                    <Visibility />
                </IconButton>
                <IconButton
                    edge="end"
                    aria-label="translate"
                    onClick={() => onTranslate(problem)}
                    sx={{ mr: 1 }}
                    color="primary"
                >
                    <Translate />
                </IconButton>
                <IconButton
                    edge="end"
                    aria-label="edit"
                    onClick={() => onEdit(problem)}
                    sx={{ mr: 1 }}
                >
                    <Edit />
                </IconButton>
                <IconButton
                    edge="end"
                    aria-label="edit content"
                    onClick={() => onEditContent(problem)}
                    sx={{ mr: 1 }}
                    color="secondary"
                >
                    <Article />
                </IconButton>
                <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={(e) => onMenuOpen(e, problem)}
                >
                    <MoreVert />
                </IconButton>
            </ListItemSecondaryAction>
        </ListItem>
    );
};

AlgorithmicProblemListItem.propTypes = {
    problem: PropTypes.shape({
        _id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        problemUrl: PropTypes.string,
        difficulty: PropTypes.oneOf(['easy', 'medium', 'hard']),
        isReadyToServe: PropTypes.bool,
        language: PropTypes.shape({
            name: PropTypes.string
        }),
        level: PropTypes.shape({
            _id: PropTypes.string,
            name: PropTypes.string
        }),
        tags: PropTypes.arrayOf(PropTypes.string),
        task: PropTypes.shape({
            _id: PropTypes.string
        })
    }).isRequired,
    orderNumber: PropTypes.number.isRequired,
    onView: PropTypes.func.isRequired,
    onEdit: PropTypes.func.isRequired,
    onTranslate: PropTypes.func.isRequired,
    onEditContent: PropTypes.func.isRequired,
    onMenuOpen: PropTypes.func.isRequired
};

export default AlgorithmicProblemListItem;

